import { useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Band } from '../../types/database';
import { GENRE_OPTIONS } from '../../lib/utils';
import CountrySelect from '../ui/CountrySelect';
import { getCountryByName, type Country } from '../../lib/countries';

interface Props {
  band: Band;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditBandModal({ band, onClose, onSuccess }: Props) {
  const [name, setName] = useState(band.name);
  const [description, setDescription] = useState(band.description ?? '');
  const [city, setCity] = useState(() => {
    // Extract city from "City, Country" format
    const loc = band.location_name ?? '';
    const parts = loc.split(', ');
    return parts.length > 1 ? parts.slice(0, -1).join(', ') : '';
  });
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(
    getCountryByName(band.location_country ?? '') ?? null
  );
  const [website, setWebsite] = useState(band.website ?? '');
  const [contactEmail, setContactEmail] = useState(band.contact_email ?? '');
  const [genres, setGenres] = useState<string[]>(band.genres);
  const [isPublic, setIsPublic] = useState(band.is_public);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function toggleGenre(genre: string) {
    setGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre]
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    let logoUrl = band.logo_url;

    if (logoFile) {
      const fileExt = logoFile.name.split('.').pop();
      const filePath = `${band.id}/logo.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('band-logos')
        .upload(filePath, logoFile, { upsert: true });

      if (uploadError) {
        setError('Failed to upload logo: ' + uploadError.message);
        setLoading(false);
        return;
      }

      const { data: urlData } = supabase.storage.from('band-logos').getPublicUrl(filePath);
      logoUrl = urlData.publicUrl;
    }

    const { error: updateError } = await supabase
      .from('bands')
      .update({
        name,
        description: description || null,
        location_name: city.trim() && selectedCountry ? `${city.trim()}, ${selectedCountry.name}` : selectedCountry?.name || null,
        location_country: selectedCountry?.name || null,
        website: website || null,
        contact_email: contactEmail || null,
        genres,
        is_public: isPublic,
        logo_url: logoUrl,
      })
      .eq('id', band.id);

    if (updateError) {
      setError(updateError.message);
      setLoading(false);
    } else {
      onSuccess();
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-800 sticky top-0 bg-gray-900">
          <h2 className="text-lg font-semibold">Edit Band Profile</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="bg-red-900/50 border border-red-700 text-red-200 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Band Name *</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Logo</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-800 file:text-gray-300 hover:file:bg-gray-700"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Country</label>
            <CountrySelect
              value={selectedCountry?.name ?? ''}
              onChange={(c) => setSelectedCountry(c)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">City / Town</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. St Peter Port, London"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Website</label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Contact Email</label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Genres</label>
            <div className="flex flex-wrap gap-2">
              {GENRE_OPTIONS.map((genre) => (
                <button
                  key={genre}
                  type="button"
                  onClick={() => toggleGenre(genre)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                    genres.includes(genre)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-gray-50 hover:bg-gray-700'
                  }`}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="rounded border-gray-700 bg-gray-800 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-300">Band page is publicly visible</span>
          </label>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-800 hover:bg-gray-700 text-gray-50 py-2 px-4 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg transition"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
